import React, { useState, useMemo } from 'react';
import Typography from '@mui/material/Typography';
import TablePagination from '@mui/material/TablePagination';
import classnames from 'classnames';
import { User, Activity } from 'lucide-react';
import { Icon } from '@iconify/react';
import { Tooltip } from '@mui/material';
import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    useReactTable,
    getSortedRowModel
} from '@tanstack/react-table';

import ScoreCard from '../../components/common/ScoreCard/ScoreCard';
import tableStyles from '../../components/common/Template_Layouts/@core/styles/table.module.css';

// Import helpers and components from LeadActivityModule
import { 
    ActivityMetaHover,
    getAvatarStyle,
    getInitials,
    formatDisplayDate,
    formatActivityTime,
    getRemoteIcon
} from './LeadActivityModule';

const columnHelper = createColumnHelper();

const LeadActivityListTable = ({
    data,
    loading,
    totalRecords,
    currentPage,
    onPageChange,
    itemsPerPage,
    onItemsPerPageChange,
    onViewActivity,
    onViewLead,
    sortConfig,
    onSortChange,
    isHiddenField = () => false
}) => {
    // We pass our external data to the table
    const columns = useMemo(() => {
        const cols = [
            columnHelper.accessor('details', {
            header: 'ACTIVITY',
            cell: ({ row }) => {
                const act = row.original;
                return (
                    <div className="flex items-start gap-3 py-1 cursor-pointer" onClick={() => onViewActivity && onViewActivity(act)}>
                        <ActivityMetaHover activity={act}>
                            <img
                                src={getRemoteIcon(act.EventName)}
                                alt=""
                                className={(act.EventName || '').toLowerCase().includes('whatsapp') ? 'w-6 h-6 object-contain' : 'w-6 h-6 object-contain'}
                                onError={(e) => { e.target.src = "https://kit19.com/assets/custom/UserCRMCampaign/Images/LeadActivityIMG/lifecycle.png" }}
                            />
                        </ActivityMetaHover>
                        <div className="flex flex-col min-w-0 max-w-[400px]">
                            <Typography 
                                sx={{ 
                                    color: '#666cff', 
                                    fontSize: '14px', 
                                    fontWeight: 600,
                                    whiteSpace: 'normal',
                                    wordBreak: 'break-word',
                                    textDecoration: 'underline'
                                }} 
                                className="cursor-pointer"
                            >

                                {act.EventName || 'New Interaction'}
                            </Typography>
                            {!isHiddenField('ActivityDescription') && act.EventDescription && (
                                <Typography 
                                    sx={{ 
                                        fontSize: '12px', 
                                        color: '#64748b',
                                        whiteSpace: 'normal',
                                        wordBreak: 'break-word'
                                    }} 
                                    className="mt-0.5"
                                >
                                    {act.EventDescription}
                                </Typography>
                            )}
                            <Typography sx={{ fontSize: '11px', color: '#94a3b8', fontWeight: 500, mt: '2px' }}>
                                {formatActivityTime(act.EventTime)}
                            </Typography>
                        </div>
                    </div>
                );
            }
        }),
        columnHelper.accessor('customer', {
            header: 'CUSTOMER',
            cell: ({ row }) => {
                const act = row.original;
                return (
                    <div className="flex items-center gap-3 py-1">
                        <ScoreCard
                            leadId={act.LeadID}
                            mobileNo={act.Mobileno}
                            personName={act.PersonName}
                            initialScore={act.CurrentScore}
                        >

                            <div className="flex items-center justify-center rounded-full shrink-0 relative cursor-pointer" 
                                style={{
                                    width: '36px', height: '36px',
                                    backgroundColor: getAvatarStyle(act.PersonName).bg,
                                    color: getAvatarStyle(act.PersonName).color,
                                    fontWeight: 'bold', fontSize: '14px'
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onViewLead && onViewLead(act.LeadID || act.EnquiryId);
                                }}
                            >
                                {getInitials(act.PersonName)}
                            </div>
                        </ScoreCard>
                        <div className="flex flex-col min-w-0">
                            <Typography 
                                sx={{ 
                                    color: '#666cff', 
                                    fontSize: '13.5px', 
                                    fontWeight: 600,
                                    textDecoration: 'underline'
                                }} 
                                className="truncate cursor-pointer"

                                onClick={(e) => {
                                    e.stopPropagation();
                                    onViewLead && onViewLead(act.LeadID || act.EnquiryId);
                                }}
                            >
                                {act.PersonName || act.Mobileno || 'No Name'}
                            </Typography>
                            {act.Mobileno && (
                                <Typography sx={{ fontSize: '11px', color: '#94a3b8', fontWeight: 500, mt: '2px' }}>
                                    {act.Mobileno}
                                </Typography>
                            )}
                        </div>
                    </div>
                );
            }
        }),
        columnHelper.accessor('EventDate', {
            header: 'DATE',
            cell: ({ row }) => {
                const act = row.original;
                return (
                    <Typography sx={{ fontSize: '13px', color: '#475569', fontWeight: 500 }}>
                        {formatDisplayDate(act.EventDate)}
                    </Typography>
                );
            }
        }),
        columnHelper.accessor('owner', {
            header: 'OWNER',
            cell: ({ row }) => {
                const act = row.original;
                return (
                    <div className="flex items-center gap-2 py-1">
                        <div className="flex items-center justify-center rounded-full bg-slate-50 border-2 border-slate-200 text-slate-500 shrink-0" style={{ width: '32px', height: '32px' }}>
                            <User size={16} />
                        </div>
                        <Typography sx={{ fontSize: '13.5px', color: '#334155', fontWeight: 600 }}>
                            {act.UserName || 'N/A'}
                        </Typography>
                    </div>
                );
            }
        })
        ];

        return cols.filter(col => {
            if (col.id === 'details') return !isHiddenField('ActivityName');
            if (col.id === 'customer') return !isHiddenField('PersonName');
            if (col.id === 'EventDate') return !isHiddenField('EventDate');
            if (col.id === 'owner') return !isHiddenField('Owner');
            return true;
        });
    }, [onViewActivity, onViewLead, isHiddenField]);

    const table = useReactTable({
        data: data || [],
        columns,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        manualPagination: true,
        manualSorting: true
    });

    const [jumpPage, setJumpPage] = useState(currentPage || 1);

    return (
        <div className="flex flex-col w-full h-full bg-white relative">
            {loading && (
                <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/50 backdrop-blur-[1px]">
                    <div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
                </div>
            )}
            
            <div className="flex-1 overflow-auto">
                <table className={tableStyles.table}>
                    <thead className={tableStyles.thead}>
                        {table.getHeaderGroups().map(headerGroup => (
                            <tr key={headerGroup.id}>
                                {headerGroup.headers.map(header => (
                                    <th 
                                        key={header.id}
                                        onClick={() => {
                                            // Optional: wire up to external sortConfig if needed
                                            if (onSortChange && header.column.getCanSort()) {
                                                onSortChange(header.column.id);
                                            }
                                        }}
                                        className={classnames({
                                            'cursor-pointer select-none': header.column.getCanSort(),
                                        })}
                                    >
                                        {header.isPlaceholder ? null : (
                                            <div className={classnames('flex items-center gap-2', {
                                                'justify-between': header.column.getCanSort()
                                            })}>
                                                <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#334155', letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                                                    {flexRender(header.column.columnDef.header, header.getContext())}
                                                </Typography>
                                                {header.column.getCanSort() && (
                                                    <Tooltip title={`Sort by ${header.column.columnDef.header}`} arrow>
                                                        <div className="flex flex-col -gap-1">
                                                            {sortConfig?.key === header.column.id ? (
                                                                sortConfig.direction === 'asc' ? (
                                                                    <Icon icon="ri:arrow-up-s-fill" className="text-[#3b82f6]" width="20" height="20" />
                                                                ) : (
                                                                    <Icon icon="ri:arrow-down-s-fill" className="text-[#3b82f6]" width="20" height="20" />
                                                                )
                                                            ) : (
                                                                <Icon icon="ri:arrow-up-down-fill" className="text-[#94a3b8]" width="18" height="18" />
                                                            )}
                                                        </div>
                                                    </Tooltip>
                                                )}
                                            </div>
                                        )}
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    {data.length === 0 && !loading ? (
                        <tbody>
                            <tr>
                                <td colSpan={columns.length} className="text-center py-10 opacity-50">
                                    No activities found
                                </td>
                            </tr>
                        </tbody>
                    ) : (
                        <tbody>
                            {table.getRowModel().rows.map(row => (
                                <tr key={row.id} className="hover:bg-slate-50 transition-colors">
                                    {row.getVisibleCells().map(cell => (
                                        <td key={cell.id} className="py-2 px-4 border-b border-slate-100">
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    )}
                </table>
            </div>

            <div className="flex items-center justify-end px-4 gap-4 border-t border-slate-200 bg-white py-1">
                <div className="flex items-center gap-2">
                    <Typography variant="body2" className="text-xs opacity-70">
                        Go to page
                    </Typography>
                    <input
                        type="number"
                        value={jumpPage}
                        min={1}
                        max={Math.ceil((totalRecords || 0) / (itemsPerPage || 20))}
                        onChange={e => setJumpPage(e.target.value)}
                        onKeyDown={e => {
                            if (e.key === 'Enter') {
                                const page = Math.max(1, Math.min(Number(jumpPage), Math.ceil((totalRecords || 0) / (itemsPerPage || 20))));
                                onPageChange && onPageChange(page);
                                setJumpPage(page);
                            }
                        }}
                        onBlur={() => {
                            const page = Math.max(1, Math.min(Number(jumpPage), Math.ceil((totalRecords || 0) / (itemsPerPage || 20))));
                            onPageChange && onPageChange(page);
                            setJumpPage(page);
                        }}
                        className="w-12 h-8 text-center text-sm border rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    />
                </div>
                <TablePagination
                    rowsPerPageOptions={[10, 20, 25, 50, 100]}
                    component="div"
                    count={totalRecords || 0}
                    rowsPerPage={itemsPerPage || 20}
                    page={(currentPage || 1) - 1}
                    onPageChange={(_, page) => {
                        onPageChange && onPageChange(page + 1);
                        setJumpPage(page + 1);
                    }}
                    onRowsPerPageChange={e => {
                        onItemsPerPageChange && onItemsPerPageChange(Number(e.target.value));
                    }}
                    className="border-none"
                    sx={{
                        '.MuiTablePagination-toolbar': { minHeight: '40px', paddingLeft: '16px' },
                        '.MuiTablePagination-selectLabel': { margin: 0, fontSize: '13px' },
                        '.MuiTablePagination-displayedRows': { margin: 0, fontSize: '13px' }
                    }}
                />
            </div>
        </div>
    );
};

export default LeadActivityListTable;
